import React from 'react';
import { Dropdown, Button, Tag, message } from 'antd';
import type { MenuProps } from 'antd';
import { ChevronDown, Building2, Check } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAccountClaims } from '../hooks/useAccountClaims';
import { accountsApi } from '../services/accountsApi';

/**
 * <AccountSwitcher>
 *
 * Header dropdown that lets users with multiple linked accounts hop between
 * them. Renders nothing when the user only has one account — keeps the header
 * clean for the 95% case (single-tenant users).
 *
 * On switch: POST /auth/switch-account, replace the JWT in storage, clear the
 * react-query cache (so we don't leak stale tenant data), and hard-redirect
 * to /dashboard. Hard reload is intentional — many of our zustand slices are
 * primed at boot from the JWT, easier than threading a re-init through.
 */
const AccountSwitcher: React.FC = () => {
  const { availableAccounts, currentAccountId } = useAccountClaims();
  const queryClient = useQueryClient();

  const switchMutation = useMutation({
    mutationFn: (accountId: string) => accountsApi.switchAccount(accountId),
    onSuccess: (data) => {
      // Backend returns OAuth-style snake_case (access_token); there is no
      // global response camelCasing, so read the key as-is.
      localStorage.setItem('accessToken', data.access_token);
      localStorage.removeItem('user');
      if (data.current_tenant_id) {
        localStorage.setItem('janus.activeTenant', String(data.current_tenant_id));
      } else {
        localStorage.removeItem('janus.activeTenant');
      }
      queryClient.clear();
      message.success('Conta alterada com sucesso');
      // Hard redirect — see comment above.
      window.location.href = '/';
    },
    onError: () => {
      message.error('Falha ao trocar de conta. Tente novamente.');
    },
  });

  if (!availableAccounts || availableAccounts.length <= 1) {
    return null;
  }

  const items: MenuProps['items'] = availableAccounts.map((acc) => ({
    key: acc.id,
    label: (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 220 }}>
        <Building2 size={14} />
        <span style={{ flex: 1 }}>{acc.name || acc.id}</span>
        <Tag
          color={
            acc.role === 'account_owner'
              ? 'gold'
              : acc.role === 'platform'
              ? 'purple'
              : 'blue'
          }
        >
          {acc.role === 'account_owner'
            ? 'Proprietário'
            : acc.role === 'platform'
            ? 'Janus Admin'
            : 'Membro'}
        </Tag>
        {acc.id === currentAccountId && <Check size={14} color="#10B981" />}
      </div>
    ),
    onClick: () => {
      if (acc.id !== currentAccountId) {
        switchMutation.mutate(acc.id);
      }
    },
  }));

  const current = availableAccounts.find((a) => a.id === currentAccountId);

  return (
    <Dropdown menu={{ items }} trigger={['click']} disabled={switchMutation.isPending}>
      <Button
        type="text"
        size="small"
        loading={switchMutation.isPending}
        icon={<Building2 size={14} />}
      >
        <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {current?.name || 'Trocar conta'}
        </span>
        <ChevronDown size={14} />
      </Button>
    </Dropdown>
  );
};

export default AccountSwitcher;
